import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchDuplicateGroup, archiveDevice } from '../api/duplicates';
import { retireDevice, deleteDevice, forceDeleteDevice, deviceDeletionImpact } from '../api/devices';
import { ApiError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { DuplicateGroup, DeviceRow, Env } from '../types';

// Fields whose shared value is what makes two records look like the same unit.
const KEY_FIELDS = new Set(['deviceName', 'chargePointSerial', 'iccid', 'imsi', 'meterSerial', 'coords']);

function coordStr(d: DeviceRow): string {
  return d.latitude != null && d.longitude != null ? `${d.latitude}, ${d.longitude}` : '';
}

const ROWS: Array<{ key: string; label: string; get: (d: DeviceRow) => string }> = [
  { key: 'deviceName', label: 'Nom', get: (d) => d.deviceName || '' },
  { key: 'deviceSn', label: 'N. serie', get: (d) => d.deviceSn || '' },
  { key: 'deviceType', label: 'Type', get: (d) => d.deviceType || '' },
  { key: 'chargePointSerial', label: 'N. serie materiel', get: (d) => d.chargePointSerial || '' },
  { key: 'iccid', label: 'ICCID (SIM)', get: (d) => d.iccid || '' },
  { key: 'imsi', label: 'IMSI (SIM)', get: (d) => d.imsi || '' },
  { key: 'meterSerial', label: 'N. serie compteur', get: (d) => d.meterSerial || '' },
  { key: 'coords', label: 'Coordonnees', get: coordStr },
  { key: 'hardwareVersion', label: 'HW', get: (d) => d.hardwareVersion || '' },
  { key: 'firmwareVersion', label: 'FW', get: (d) => d.firmwareVersion || '' },
  { key: 'deviceStatus', label: 'Statut', get: (d) => d.deviceStatus || '' },
  { key: 'owner', label: 'Client', get: (d) => d.ownerName || d.ownerEmail || '' },
  { key: 'place', label: 'Station', get: (d) => d.placeName || d.placeAddress || '' },
  { key: 'createdAt', label: 'Cree le', get: (d) => (d.createdAt ? d.createdAt.slice(0, 19) : '') },
];

export function DuplicatesPage() {
  const { env: envParam, deviceId } = useParams<{ env: string; deviceId: string }>();
  const env = (envParam === 'staging' ? 'staging' : 'prod') as Env;
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'operator' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState<DuplicateGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const anchorId = Number(deviceId);

  function load() {
    if (!Number.isInteger(anchorId)) return;
    setLoading(true);
    setError(null);
    fetchDuplicateGroup(anchorId, env)
      .then((r) => setData(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  useEffect(load, [deviceId, env]);

  async function onArchive(d: DeviceRow) {
    const label = d.deviceName || d.deviceSn || `#${d.deviceId}`;
    if (!window.confirm(`Archiver "${label}" comme doublon ?\n\nL'enregistrement sera masque du backoffice (reversible). La borne wattelzce n'est pas supprimee.`)) {
      return;
    }
    const reason = window.prompt('Motif (optionnel) :', 'doublon') ?? '';
    setBusy(d.deviceId);
    try {
      await archiveDevice(d.deviceId, env, reason);
      if (d.deviceId === anchorId) navigate('/inventaire');
      else load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onRetire(d: DeviceRow) {
    if (!d.deviceSn) return;
    const label = d.deviceName || d.deviceSn;
    if (!window.confirm(`Retirer "${label}" du service (statut inactif) ? L'historique est conserve.`)) return;
    setBusy(d.deviceId);
    try {
      await retireDevice(d.deviceSn, d.env);
      load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function finishDelete(d: DeviceRow) {
    if (d.deviceId === anchorId) navigate('/inventaire');
    else load();
  }

  // Reinforced FORCE delete: shows exactly what will be destroyed + requires typing the name.
  async function forceDelete(d: DeviceRow, label: string) {
    let impactText = '(details indisponibles)';
    try {
      const i = (await deviceDeletionImpact(d.deviceSn as string, d.env)).data;
      impactText =
        `- ${i.transactions} transaction(s)\n` +
        `- ${i.chargingTransactions} session(s) de charge (+ facturation liee)\n` +
        `- ${i.connectors} connecteur(s)\n` +
        `- ${i.reservations} reservation(s)\n` +
        `- ${i.meterValues} releve(s) compteur`;
    } catch {
      /* garde le texte par defaut */
    }
    const ok = window.confirm(
      `SUPPRESSION FORCEE de "${label}"\n\n` +
        `Cette borne a un historique. Seront DEFINITIVEMENT supprimes :\n${impactText}\n` +
        `+ les logs OCPP/MQTT associes.\n\n` +
        `ACTION IRREVERSIBLE — l'historique de facturation sera perdu. Continuer ?`
    );
    if (!ok) return;
    const typed = window.prompt(`Confirmation renforcee — tapez le nom EXACT de la borne pour supprimer :\n${label}`);
    if (typed !== label) {
      alert('Nom incorrect : suppression annulee.');
      return;
    }
    setBusy(d.deviceId);
    try {
      await forceDeleteDevice(d.deviceSn as string, d.env);
      finishDelete(d);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(d: DeviceRow) {
    if (!d.deviceSn) return;
    const label = d.deviceName || d.deviceSn;
    if (!window.confirm(`Supprimer definitivement "${label}" en base ? Irreversible (refuse si la borne a un historique).`)) return;
    setBusy(d.deviceId);
    try {
      await deleteDevice(d.deviceSn, d.env);
      finishDelete(d);
      setBusy(null);
    } catch (e) {
      setBusy(null);
      if ((e as ApiError).status === 409) await forceDelete(d, label);
      else alert((e as Error).message);
    }
  }

  const writesOn = !!data?.writesEnabled;

  const devices = useMemo(() => (data ? [data.anchor, ...data.group] : []), [data]);

  // Count values per key field to highlight the cells that are shared across records.
  const sharedCounts = useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    for (const row of ROWS) {
      if (!KEY_FIELDS.has(row.key)) continue;
      const c: Record<string, number> = {};
      for (const d of devices) {
        const v = row.get(d);
        if (v) c[v] = (c[v] ?? 0) + 1;
      }
      counts[row.key] = c;
    }
    return counts;
  }, [devices]);

  const envPill = env === 'prod' ? 'pill-env-prod' : 'pill-env-staging';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/inventaire" className="t-link hover:underline text-sm">
          &larr; Vue generale
        </Link>
        <span className={envPill}>
          <span className="pill-dot" />
          {env}
        </span>
      </div>

      <h1 className="page-title">Doublons potentiels</h1>

      {loading && <div className="text-[13px] t-muted">Chargement...</div>}
      {error && !loading && <div className="text-red-400">{error}</div>}
      {!loading && !error && data && data.group.length === 0 && (
        <div className="text-[13px] t-muted">Aucun doublon pour cet equipement.</div>
      )}

      {!loading && !error && data && data.group.length > 0 && (
        <>
          <p className="text-[13px] t-muted">
            <span className="num">{devices.length}</span> equipements partagent un ou plusieurs identifiants. Les
            valeurs communes sont surlignees.
          </p>
          {canEdit && !writesOn && (
            <div className="text-[13px] rounded-md border note-amber px-3 py-2">
              Profil editeur : la resolution (archivage du doublon) sera disponible une fois l'ecriture activee sur cet
              environnement.
            </div>
          )}
          {canEdit && writesOn && (
            <p className="text-xs t-muted">
              <b>Archiver</b> : masque l'enregistrement du backoffice (reversible, wattelzce intacte) ·
              <b> Retirer</b> : statut « inactif » (wattelzce, historique conserve) ·
              <b> Supprimer</b> (admin) : DELETE en base, refuse si la borne a un historique.
            </p>
          )}
          <div className="panel p-0 overflow-hidden">
            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="th left-0 z-20">Champ</th>
                    {devices.map((d, i) => (
                      <th
                        key={`${d.env}-${d.deviceId}`}
                        className="th align-top normal-case tracking-normal text-[13px]"
                      >
                        <div className="flex items-center gap-2">
                          {d.deviceSn ? (
                            <Link
                              to={`/devices/${encodeURIComponent(d.deviceSn)}?env=${d.env}`}
                              className="t-link hover:underline"
                            >
                              {d.deviceName || d.deviceSn}
                            </Link>
                          ) : (
                            <span className="t-strong">{d.deviceName || `#${d.deviceId}`}</span>
                          )}
                          {i === 0 && (
                            <span className="pill pill-sky">
                              <span className="pill-dot" />
                              consulte
                            </span>
                          )}
                        </div>
                        {canEdit && writesOn && (
                          <div className="mt-2 flex flex-col gap-1 items-start">
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() => onArchive(d)}
                              className="btn-outline h-8 w-full"
                              title="Masque l'enregistrement du backoffice (reversible). Ne supprime pas la borne wattelzce."
                            >
                              {busy === d.deviceId ? '...' : 'Archiver (doublon)'}
                            </button>
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() => onRetire(d)}
                              className="btn-outline h-8 w-full"
                              title="Passe la borne en statut inactif (wattelzce). Historique conserve."
                            >
                              Retirer (inactif)
                            </button>
                            {isAdmin && (
                              <button
                                type="button"
                                disabled={busy !== null}
                                onClick={() => onDelete(d)}
                                className="btn-danger h-8 w-full"
                                title="DELETE en base. Irreversible. Refuse si la borne a un historique."
                              >
                                Supprimer
                              </button>
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.key} className="tr-hover">
                      <td className="td sticky left-0 bg-surface font-medium t-muted">{row.label}</td>
                      {devices.map((d) => {
                        const v = row.get(d);
                        const shared = KEY_FIELDS.has(row.key) && !!v && (sharedCounts[row.key]?.[v] ?? 0) >= 2;
                        return (
                          <td
                            key={`${d.env}-${d.deviceId}-${row.key}`}
                            className={`td ${shared ? 'hl-amber font-semibold' : ''}`}
                          >
                            {v || '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
